import posthog from "posthog-js";
import axios from "axios";
import { identify as posthog__identify } from "@/helpers/posthog";

import companyService from "@/services/company";
import serviceHandler from "@/helpers/handlers/service";
import { AccountType } from "@/enums/account-type";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";

const isWizardCompleted = companies => {
  if (
    companies.length &&
    (companies.every(
      company => company.company_user.type !== AccountType.OWNER
    ) ||
      companies.findIndex(
        company =>
          company.company_user.type === AccountType.OWNER &&
          company.is_master &&
          company.wizard_completed
      ) > -1)
  ) {
    return true;
  }

  if (companies.length === 1) {
    return companies[0].wizard_completed;
  }

  return !!companies.length;
};

const fetchCompaniesWithAccessCleanup = async () => {
  try {
    return await serviceHandler(companyService.getCompanies());
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    localStorage.removeItem(LOCAL_STORAGE_KEYS.COMPANY_ID); // Handle case when access to company stored in local storage is revoked
    return await serviceHandler(companyService.getCompanies());
  }
};

export default {
  async refreshSubscriptionSummary({ state, dispatch }) {
    const companyId = state.company.id;
    const { data } = await companyService.getCompanyDetails(companyId);
    if (state.company.id === companyId) {
      await dispatch("setCompany", { company: data });
    }
  },
  async fetchCategories({ commit }) {
    return await serviceHandler(companyService.getCategories()).then(item => {
      commit("SET_CATEGORIES", item);
    });
  },

  async fetchCompanies({ dispatch }) {
    const result = await fetchCompaniesWithAccessCleanup();
    await dispatch("setCompanies", result || []);
  },

  async setCompanyParent({ state, commit }, companyId) {
    const result = await serviceHandler(
      companyService.getCompanyParent(companyId)
    );
    commit("SET_PARENT", { id: state.company.parent, ...result });
  },

  async clearCompanyParent({ state, commit }) {
    commit("SET_PARENT", null);
  },

  async fetchCompanyStatus({ state, commit, dispatch }) {
    if (state.id) {
      return state;
    }
    dispatch("fetchCompanies");
  },

  async setWizardCompletionStatus({ state, commit }, status) {
    await serviceHandler(
      companyService.patchCompanyData({ wizard_completed: status }, state.id)
    );

    commit("SET_WIZARD_COMPLETION_STATUS", status);
  },

  setCompanyId({ commit }, payload) {
    commit("SET_COMPANY_ID", payload);
  },

  async setCompanyById({ state, dispatch }, companyId) {
    if (state.company && state.company.id === companyId) {
      return;
    }
    const company = state.companies.find(({ id }) => id === companyId);
    if (company) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_ID, companyId);
      const companyDetails = await serviceHandler(
        companyService.getCompanyDetails(companyId)
      );
      dispatch("setCompany", { company: companyDetails });
    }
  },

  setCompany({ commit, dispatch }, { company, posthogIdentify = false }) {
    if (posthogIdentify) {
      posthog__identify(company.company_user.user_id);
    }

    posthog.people.set({
      company_id: `${company.id}`,
      parent_id: company.parent ? `${company.parent}` : null
    });

    commit("SET_COMPANY", company);
    if (posthogIdentify) {
      // Evaluate once after identifying the user, without delaying navigation.
      dispatch("settings/loadStripeSubscriptionUiFlag", null, { root: true });
    }
  },

  async updateCurrentCompany({ state, dispatch }) {
    const data = await serviceHandler(companyService.getCompany());

    if (data.length && state.company && state.company.id) {
      const foundCompany = data.find(
        company => company.id === state.company.id
      );

      if (foundCompany) {
        const company = await serviceHandler(
          companyService.getCompanyDetails(foundCompany.id)
        );
        dispatch("setCompany", { company });
      }
    }
  },

  async setCompanies({ state, dispatch, commit }, payload) {
    commit("SET_COMPANIES", payload);

    let companyId = +localStorage.getItem(LOCAL_STORAGE_KEYS.COMPANY_ID);
    if (payload.length && !payload.find(({ id }) => id === companyId)) {
      companyId = payload[0].id;
    }

    if (companyId) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_ID, companyId);
      const company = await serviceHandler(
        companyService.getCompanyDetails(companyId)
      );
      dispatch("setCompany", { company: company, posthogIdentify: true });
    }

    commit("SET_WIZARD_COMPLETION_STATUS", isWizardCompleted(payload));
  }
};
